import { trackEvent } from "../analytics/analytics";

// IM6. HEIC is the iPhone camera default and the server has no decoder for it. Every picker says
// accept="image/*", which a HEIC satisfies on a Mac, so the file went all the way to the server
// and came back as a generic "not a supported image". Refused here, by name, before the upload.
// Same wording as ImageManager.HeicError, which still guards the API and every other entry.
export const HEIC_REFUSAL =
  "HEIC photos (the iPhone format) can't be used yet. Save the photo as JPG and upload that. " +
  "On iPhone: Settings > Camera > Formats > Most Compatible.";

const HEIC_EXTENSIONS = [".heic", ".heif"];
const HEIC_TYPES = ["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"];

// Windows reports no MIME type for a HEIC at all, so the name has to be enough on its own.
export function isHeicFile(file: { name?: string; type?: string } | null | undefined): boolean {
  if (!file) return false;
  const name = (file.name ?? "").toLowerCase();
  return (
    HEIC_EXTENSIONS.some((ext) => name.endsWith(ext)) ||
    HEIC_TYPES.includes((file.type ?? "").toLowerCase())
  );
}

/**
 * Split a picked or dropped batch into what can upload and what cannot, and count the refusals.
 * `message` is ready for a snackbar and is undefined when nothing was refused.
 *
 * The trace is the only record that a HEIC was ever offered: sizing IM6 found no log anywhere
 * that kept the extension of a refused upload. `surface` goes in the label because
 * Diagnostics.Logs keeps no metadata.
 */
export function refuseHeic(
  files: File[],
  surface: string
): { accepted: File[]; refused: File[]; message?: string } {
  const refused = files.filter(isHeicFile);
  if (refused.length === 0) return { accepted: files, refused };

  trackEvent({
    type: "trace",
    category: "images",
    action: "file-rejected",
    label: `heic:${surface}`,
    value: refused.length,
    internal: true,
  });

  const names =
    refused.length === 1 ? refused[0].name : `${refused.length} photos`;
  return {
    accepted: files.filter((f) => !isHeicFile(f)),
    refused,
    message: `${names}: ${HEIC_REFUSAL}`,
  };
}
